/**
 * TestRunsRunner — lista test runs, lanzar nuevo run, drawer con detalle de resultados.
 *
 * Endpoints:
 *   GET    /api/qa/test-runs?limit=50           → { runs }
 *   GET    /api/qa/test-runs/:id/results?onlyFails=0|1 → { results }
 *   POST   /api/qa/test-runs                    → { runId }
 *   POST   /api/qa/test-runs/:id/cancel
 *   GET    /api/qa/golden                       → { data }   (para multiselect)
 *   GET    /api/qa/prompts?type=queryai|aimatcher → { data }
 *
 * Auto-refresh cada 5s si hay algún run con status=running.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { API_BASE } from '../config';
import './TestRunsRunner.css';

const ENDPOINTS = [
  { value: 'heroku', label: 'heroku' },
  { value: 'price',  label: 'price' },
  { value: 'custom', label: 'custom (URL)' },
];

function fmtDateTime(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('es-AR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
  } catch (_) { return '—'; }
}

function fmtDuration(ms) {
  if (!ms || !Number.isFinite(Number(ms))) return '—';
  const n = Number(ms);
  if (n < 1000) return `${n} ms`;
  if (n < 60000) return `${(n / 1000).toFixed(1)} s`;
  const m = Math.floor(n / 60000);
  const s = Math.floor((n % 60000) / 1000);
  return `${m}m ${s}s`;
}

function fmtCost(usd) {
  const n = Number(usd);
  if (!Number.isFinite(n)) return '—';
  return `$${n.toFixed(4)}`;
}

function fmtNumber(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return v.toLocaleString('es-AR');
}

function StatusBadge({ status }) {
  const cls = status === 'running'   ? 'qa-run-status-running'
            : status === 'completed' ? 'qa-run-status-completed'
            : status === 'error'     ? 'qa-run-status-error'
            : status === 'cancelled' ? 'qa-run-status-cancelled'
            : 'qa-run-status-cancelled';
  return <span className={`qa-run-status ${cls}`}>{status || '—'}</span>;
}

function Toast({ toast, onClose }) {
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [toast, onClose]);
  if (!toast) return null;
  return (
    <div className={`um-toast ${toast.type === 'error' ? 'um-toast-error' : ''}`}>
      {toast.message}
    </div>
  );
}

function NewRunModal({ busy, onClose, onSubmit }) {
  const [goldens, setGoldens]   = useState([]);
  const [loadingG, setLoadingG] = useState(true);
  const [errorG,   setErrorG]   = useState(null);

  const [promptsQ, setPromptsQ] = useState([]);
  const [promptsA, setPromptsA] = useState([]);
  const [loadingP, setLoadingP] = useState(true);

  const [selected, setSelected]    = useState(new Set());
  const [search,   setSearch]      = useState('');
  const [endpoint, setEndpoint]    = useState('price');
  const [customUrl,setCustomUrl]   = useState('');
  const [promptQ,  setPromptQ]     = useState('');
  const [promptA,  setPromptA]     = useState('');
  const [runName,  setRunName]     = useState(`run ${new Date().toISOString().slice(0,16).replace('T',' ')}`);
  const [intake,   setIntake]      = useState('');
  const [formError,setFormError]   = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/qa/golden`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        if (alive) {
          setGoldens(Array.isArray(j.data) ? j.data : []);
        }
      } catch (e) {
        if (alive) setErrorG(e.message || 'Error al cargar golden set');
      } finally {
        if (alive) setLoadingG(false);
      }
    })();
    (async () => {
      try {
        const [rq, ra] = await Promise.all([
          fetch(`${API_BASE}/api/qa/prompts?type=queryai`).then(r => r.ok ? r.json() : { data: [] }),
          fetch(`${API_BASE}/api/qa/prompts?type=aimatcher`).then(r => r.ok ? r.json() : { data: [] }),
        ]);
        if (alive) {
          setPromptsQ(Array.isArray(rq.data) ? rq.data : []);
          setPromptsA(Array.isArray(ra.data) ? ra.data : []);
          // preselect active
          const aq = (rq.data || []).find(p => p.is_active);
          const aa = (ra.data || []).find(p => p.is_active);
          if (aq) setPromptQ(String(aq.id));
          if (aa) setPromptA(String(aa.id));
        }
      } finally {
        if (alive) setLoadingP(false);
      }
    })();
    // load default endpoint from localStorage
    try {
      const raw = localStorage.getItem('qa_default_endpoint');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.name) setEndpoint(parsed.name);
        if (parsed?.customUrl) setCustomUrl(parsed.customUrl);
      }
    } catch (_) { /* ignore */ }
    return () => { alive = false; };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return goldens;
    return goldens.filter(g =>
      (g.product_name && g.product_name.toLowerCase().includes(q)) ||
      (g.expected_match_id_catalogo && String(g.expected_match_id_catalogo).toLowerCase().includes(q))
    );
  }, [goldens, search]);

  const toggle = (id) => {
    setSelected(s => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAllVisible = () => {
    setSelected(s => {
      const next = new Set(s);
      filtered.forEach(g => next.add(g.id));
      return next;
    });
  };

  const clearAll = () => setSelected(new Set());

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    if (selected.size === 0) {
      setFormError('Seleccioná al menos 1 item del golden set.');
      return;
    }
    if (endpoint === 'custom' && !customUrl.trim()) {
      setFormError('customUrl requerido para endpoint=custom.');
      return;
    }
    if (!runName.trim()) {
      setFormError('run_name requerido.');
      return;
    }
    try {
      await onSubmit({
        goldenIds: Array.from(selected),
        prompt_queryai_id:   promptQ ? Number(promptQ) : null,
        prompt_aimatcher_id: promptA ? Number(promptA) : null,
        endpoint,
        customUrl: endpoint === 'custom' ? customUrl.trim() : undefined,
        run_name: runName.trim(),
        intake: intake.trim() || undefined,
      });
    } catch (err) {
      setFormError(err.message || 'Error al crear run.');
    }
  };

  return (
    <div className="um-modal-overlay" onClick={onClose}>
      <div className="um-modal qa-modal-lg" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={handleSubmit}>
          <div className="um-modal-header">
            <h3>Nuevo test run</h3>
          </div>
          <div className="um-modal-body">
            {formError && <div className="um-error">{formError}</div>}

            <div className="qa-form-row">
              <div className="um-field">
                <label>Nombre del run</label>
                <input
                  type="text"
                  value={runName}
                  onChange={(e) => setRunName(e.target.value)}
                  required
                />
              </div>
              <div className="um-field">
                <label>Intake (opcional)</label>
                <input
                  type="text"
                  value={intake}
                  onChange={(e) => setIntake(e.target.value)}
                  placeholder="toma_40 / qa_test"
                />
              </div>
            </div>

            <div className="qa-form-row">
              <div className="um-field">
                <label>Endpoint LLM</label>
                <select value={endpoint} onChange={(e) => setEndpoint(e.target.value)}>
                  {ENDPOINTS.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
                </select>
              </div>
              {endpoint === 'custom' && (
                <div className="um-field">
                  <label>Custom URL</label>
                  <input
                    type="text"
                    value={customUrl}
                    onChange={(e) => setCustomUrl(e.target.value)}
                    placeholder="https://..."
                  />
                </div>
              )}
            </div>

            <div className="qa-form-row">
              <div className="um-field">
                <label>Prompt QueryAI</label>
                <select value={promptQ} onChange={(e) => setPromptQ(e.target.value)} disabled={loadingP}>
                  <option value="">— activo por default —</option>
                  {promptsQ.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.version}{p.is_active ? ' (activo)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="um-field">
                <label>Prompt AIMatcher</label>
                <select value={promptA} onChange={(e) => setPromptA(e.target.value)} disabled={loadingP}>
                  <option value="">— activo por default —</option>
                  {promptsA.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.version}{p.is_active ? ' (activo)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="um-field">
              <label>Golden items ({selected.size} seleccionados)</label>
              {errorG && <div className="um-error">{errorG}</div>}
              <div className="qa-golden-picker">
                <div className="qa-picker-toolbar">
                  <input
                    className="qa-picker-search"
                    type="search"
                    placeholder="Buscar..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  <button type="button" className="um-btn um-btn-link" onClick={selectAllVisible}>
                    Todos visibles
                  </button>
                  <button type="button" className="um-btn um-btn-link-danger" onClick={clearAll}>
                    Limpiar
                  </button>
                  <span className="qa-picker-counter">{filtered.length} / {goldens.length}</span>
                </div>
                {loadingG ? (
                  <div className="qa-golden-picker-empty">Cargando golden set...</div>
                ) : filtered.length === 0 ? (
                  <div className="qa-golden-picker-empty">Sin items.</div>
                ) : (
                  filtered.slice(0, 200).map(g => {
                    const id = `qa-pick-${g.id}`;
                    return (
                      <div key={g.id} className="qa-golden-picker-row" onClick={() => toggle(g.id)}>
                        <input
                          id={id}
                          type="checkbox"
                          checked={selected.has(g.id)}
                          onChange={() => toggle(g.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <label htmlFor={id} onClick={(e) => e.stopPropagation()}>
                          {g.product_name}
                          {g.expected_sitio && <span style={{ color: '#6b7280' }}> · {g.expected_sitio}</span>}
                        </label>
                      </div>
                    );
                  })
                )}
                {!loadingG && filtered.length > 200 && (
                  <div className="qa-golden-picker-empty" style={{ padding: 10 }}>
                    Mostrando 200 de {filtered.length}. Refiná la búsqueda para ver más.
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="um-modal-footer">
            <button type="button" className="um-btn um-btn-ghost" onClick={onClose} disabled={busy}>
              Cancelar
            </button>
            <button type="submit" className="um-btn um-btn-primary" disabled={busy || loadingG || loadingP}>
              {busy ? 'Lanzando...' : `Lanzar run (${selected.size})`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ReasoningCell({ text }) {
  const [expanded, setExpanded] = useState(false);
  if (!text) return <span style={{ color: '#9ca3af' }}>—</span>;
  const long = text.length > 140;
  return (
    <div>
      <div className={`qa-reasoning-cell${!expanded && long ? ' collapsed' : ''}`}>
        {text}
      </div>
      {long && (
        <button type="button" className="qa-expand-link" onClick={() => setExpanded(!expanded)}>
          {expanded ? '— colapsar' : '+ expandir'}
        </button>
      )}
    </div>
  );
}

function RunDetailDrawer({ run, onClose, onCancel }) {
  const [results,  setResults]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [onlyFails,setOnlyFails]= useState(false);
  const [showQuery,setShowQuery]= useState(false);

  const fetchResults = useCallback(async () => {
    if (!run) return;
    setLoading(true);
    setError(null);
    try {
      const url = `${API_BASE}/api/qa/test-runs/${encodeURIComponent(run.id)}/results?onlyFails=${onlyFails ? 1 : 0}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setResults(Array.isArray(data.results) ? data.results : []);
    } catch (e) {
      setError(e.message || 'Error al cargar resultados');
    } finally {
      setLoading(false);
    }
  }, [run, onlyFails]);

  useEffect(() => { fetchResults(); }, [fetchResults]);

  if (!run) return null;

  const passRate = run.total_cases
    ? ((Number(run.pass_count || 0) / Number(run.total_cases)) * 100).toFixed(1)
    : '—';

  return (
    <div className="qa-drawer-overlay" onClick={onClose}>
      <div className="qa-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="qa-drawer-header">
          <h3>
            Run #{run.id} — {run.run_name || '(sin nombre)'}{' '}
            <StatusBadge status={run.status} />
          </h3>
          <div style={{ display: 'flex', gap: 8 }}>
            {run.status === 'running' && (
              <button className="um-btn um-btn-danger" onClick={() => onCancel(run.id)}>
                Cancelar run
              </button>
            )}
            <button className="um-btn um-btn-ghost" onClick={fetchResults} disabled={loading}>
              Refrescar
            </button>
            <button className="um-btn um-btn-ghost" onClick={onClose}>Cerrar</button>
          </div>
        </div>
        <div className="qa-drawer-body">
          <div className="qa-drawer-meta">
            <div className="qa-meta-item">Endpoint<strong>{run.endpoint_used || '—'}</strong></div>
            <div className="qa-meta-item">Total cases<strong>{fmtNumber(run.total_cases)}</strong></div>
            <div className="qa-meta-item">Pass rate<strong>{passRate}%</strong></div>
            <div className="qa-meta-item">Cost total<strong>{fmtCost(run.total_cost_usd)}</strong></div>
            <div className="qa-meta-item">Pass / Fail / Err
              <strong>
                <span style={{ color: '#065f46' }}>{run.pass_count || 0}</span>
                {' / '}
                <span style={{ color: '#991b1b' }}>{run.fail_count || 0}</span>
                {' / '}
                <span style={{ color: '#b45309' }}>{run.error_count || 0}</span>
              </strong>
            </div>
            <div className="qa-meta-item">Tokens (in+out)
              <strong>{fmtNumber((run.total_tokens_in || 0) + (run.total_tokens_out || 0))}</strong>
            </div>
            <div className="qa-meta-item">Duration total<strong>{fmtDuration(run.total_duration_ms)}</strong></div>
            <div className="qa-meta-item">Lanzado<strong>{fmtDateTime(run.ran_at)}</strong></div>
            <div className="qa-meta-item">Por<strong>{run.ran_by_email || '—'}</strong></div>
            <div className="qa-meta-item">Prompt queryai<strong>{run.prompt_queryai_version || '—'}</strong></div>
            <div className="qa-meta-item">Prompt aimatcher<strong>{run.prompt_aimatcher_version || '—'}</strong></div>
            <div className="qa-meta-item">Intake<strong>{run.intake || '—'}</strong></div>
          </div>

          {run.error_message && (
            <div className="um-error" style={{ marginBottom: 12 }}>
              <strong>Error del run:</strong> {run.error_message}
            </div>
          )}

          <div className="qa-drawer-toolbar">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <label className="um-checkbox-row" style={{ margin: 0 }}>
                <input
                  type="checkbox"
                  checked={onlyFails}
                  onChange={(e) => setOnlyFails(e.target.checked)}
                />
                <span style={{ fontSize: '0.82rem', color: '#374151' }}>Solo fallas</span>
              </label>
              <label className="um-checkbox-row" style={{ margin: 0 }}>
                <input
                  type="checkbox"
                  checked={showQuery}
                  onChange={(e) => setShowQuery(e.target.checked)}
                />
                <span style={{ fontSize: '0.82rem', color: '#374151' }}>Mostrar query</span>
              </label>
            </div>
            <span style={{ fontSize: '0.82rem', color: '#6b7280' }}>
              {loading ? '...' : `${results.length} resultados`}
            </span>
          </div>

          {error && <div className="um-error">{error}</div>}

          {loading ? (
            <div className="um-loading">Cargando resultados...</div>
          ) : results.length === 0 ? (
            <div className="um-empty">Sin resultados.</div>
          ) : (
            <table className="qa-results-table">
              <thead>
                <tr>
                  <th>Producto</th>
                  <th>Expected</th>
                  <th>Actual</th>
                  <th className="qa-num">Score</th>
                  <th>Pass</th>
                  {showQuery && <th>Query</th>}
                  <th>Reasoning</th>
                  <th className="qa-num">Tokens</th>
                  <th className="qa-num">Cost</th>
                  <th className="qa-num">Dur</th>
                </tr>
              </thead>
              <tbody>
                {results.map(r => (
                  <tr key={r.id}>
                    <td title={r.product_name}>{r.product_name || '—'}</td>
                    <td>
                      <code className="qa-mono">{r.expected_match_id_catalogo || '—'}</code>
                      {r.expected_score_min != null && (
                        <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                          min: {Number(r.expected_score_min).toFixed(0)}
                        </div>
                      )}
                    </td>
                    <td>
                      <code className="qa-mono">{r.actual_match_id_catalogo || '—'}</code>
                    </td>
                    <td className="qa-num">
                      {r.actual_score != null ? Number(r.actual_score).toFixed(1) : '—'}
                    </td>
                    <td>
                      {r.error_message
                        ? <span className="qa-pass-no" style={{ background: '#fef3c7', color: '#92400e' }}>err</span>
                        : r.pass
                          ? <span className="qa-pass-yes">PASS</span>
                          : <span className="qa-pass-no">FAIL</span>}
                    </td>
                    {showQuery && (
                      <td className="qa-query-cell" title={r.query_generated || ''}>
                        {r.query_generated || '—'}
                      </td>
                    )}
                    <td>
                      {r.error_message
                        ? <span className="qa-error-cell">{r.error_message}</span>
                        : <ReasoningCell text={r.llm_reasoning} />}
                    </td>
                    <td className="qa-num">
                      {fmtNumber((r.tokens_in || 0) + (r.tokens_out || 0))}
                    </td>
                    <td className="qa-num">{fmtCost(r.cost_usd)}</td>
                    <td className="qa-num">{fmtDuration(r.duration_ms)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default function TestRunsRunner() {
  const [runs,    setRuns]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [toast,   setToast]   = useState(null);
  const [newModal,setNewModal]= useState({ open: false, busy: false });
  const [drawerRun,setDrawerRun]= useState(null);
  const pollRef = useRef(null);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
  }, []);

  const fetchRuns = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/qa/test-runs?limit=50`);
      if (res.status === 401 || res.status === 403) {
        throw new Error('Sin permisos para ver test runs.');
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const list = Array.isArray(data.runs) ? data.runs : [];
      setRuns(list);
      setError(null);
      // si el drawer está abierto, refrescar metadata del run actual
      setDrawerRun(prev => prev ? (list.find(r => r.id === prev.id) || prev) : prev);
    } catch (e) {
      setError(e.message || 'Error al cargar runs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRuns(); }, [fetchRuns]);

  // Auto-refresh cada 5s si hay alguno corriendo
  useEffect(() => {
    const anyRunning = runs.some(r => r.status === 'running');
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (anyRunning) {
      pollRef.current = setInterval(fetchRuns, 5000);
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [runs, fetchRuns]);

  const handleCreate = async (payload) => {
    setNewModal(s => ({ ...s, busy: true }));
    try {
      const res = await fetch(`${API_BASE}/api/qa/test-runs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.status === 401 || res.status === 403) throw new Error('Sin permisos (superadmin).');
      if (!res.ok) {
        const j = await res.json().catch(() => null);
        throw new Error(j?.error || `HTTP ${res.status}`);
      }
      const j = await res.json().catch(() => ({}));
      setNewModal({ open: false, busy: false });
      showToast(`Run #${j.runId || ''} lanzado`);
      await fetchRuns();
    } catch (err) {
      setNewModal(s => ({ ...s, busy: false }));
      throw err;
    }
  };

  const handleCancel = async (runId) => {
    try {
      const res = await fetch(`${API_BASE}/api/qa/test-runs/${encodeURIComponent(runId)}/cancel`, {
        method: 'POST',
      });
      if (res.status === 401 || res.status === 403) throw new Error('Sin permisos.');
      if (!res.ok) {
        const j = await res.json().catch(() => null);
        throw new Error(j?.error || `HTTP ${res.status}`);
      }
      showToast(`Run #${runId} cancelado`);
      await fetchRuns();
    } catch (err) {
      showToast(err.message || 'Error al cancelar', 'error');
    }
  };

  return (
    <>
      <div className="um-card">
        <div className="um-toolbar">
          <div className="um-toolbar-left">
            <span className="um-count">
              {loading ? '...' : `${runs.length} run${runs.length === 1 ? '' : 's'}`}
            </span>
            {runs.some(r => r.status === 'running') && (
              <span style={{ fontSize: '0.78rem', color: '#1e40af' }}>
                · auto-refresh activo
              </span>
            )}
          </div>
          <div className="qa-toolbar-actions">
            <button
              className="um-btn um-btn-ghost"
              onClick={fetchRuns}
              disabled={loading}
            >
              Refrescar
            </button>
            <button
              className="um-btn um-btn-primary"
              onClick={() => setNewModal({ open: true, busy: false })}
            >
              + Nuevo run
            </button>
          </div>
        </div>

        {error && <div className="um-error" style={{ margin: 14 }}>{error}</div>}

        <div className="um-table-wrap">
          {loading ? (
            <div className="um-loading">Cargando runs...</div>
          ) : runs.length === 0 ? (
            <div className="um-empty">No hay test runs todavía.</div>
          ) : (
            <table className="um-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Run</th>
                  <th>Endpoint</th>
                  <th>Status</th>
                  <th>Cases</th>
                  <th>Resultado</th>
                  <th className="qa-cost-cell">Cost</th>
                  <th>Duración</th>
                  <th>Lanzado</th>
                  <th>Por</th>
                </tr>
              </thead>
              <tbody>
                {runs.map(r => (
                  <tr
                    key={r.id}
                    onClick={() => setDrawerRun(r)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td><code className="qa-mono">{r.id}</code></td>
                    <td>
                      <div className="qa-run-name">{r.run_name || '(sin nombre)'}</div>
                      {r.intake && (
                        <div style={{ fontSize: '0.72rem', color: '#6b7280' }}>
                          intake: {r.intake}
                        </div>
                      )}
                    </td>
                    <td><span className="qa-run-endpoint">{r.endpoint_used || '—'}</span></td>
                    <td><StatusBadge status={r.status} /></td>
                    <td>{fmtNumber(r.total_cases)}</td>
                    <td>
                      <div className="qa-run-counts">
                        <span className="qa-cnt-pass">{r.pass_count || 0}</span>
                        <span style={{ color: '#9ca3af' }}>/</span>
                        <span className="qa-cnt-fail">{r.fail_count || 0}</span>
                        <span style={{ color: '#9ca3af' }}>/</span>
                        <span className="qa-cnt-err">{r.error_count || 0}</span>
                      </div>
                    </td>
                    <td className="qa-cost-cell">{fmtCost(r.total_cost_usd)}</td>
                    <td>{fmtDuration(r.total_duration_ms)}</td>
                    <td>{fmtDateTime(r.ran_at)}</td>
                    <td style={{ fontSize: '0.78rem', color: '#6b7280' }}>{r.ran_by_email || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {newModal.open && (
        <NewRunModal
          busy={newModal.busy}
          onClose={() => newModal.busy ? null : setNewModal({ open: false, busy: false })}
          onSubmit={handleCreate}
        />
      )}

      {drawerRun && (
        <RunDetailDrawer
          run={drawerRun}
          onClose={() => setDrawerRun(null)}
          onCancel={handleCancel}
        />
      )}

      <Toast toast={toast} onClose={() => setToast(null)} />
    </>
  );
}
